// Victory animations: the round winner's worm celebrates in the arena during the
// victory lap. Ported from the winner-screens victory lab (the six picks Mathias
// approved). Same architecture as spawn-anim.ts: pure-canvas draw functions the
// worm-head renderer calls each frame with the head position and elapsed time.
//
// These are COSMETICS. Human players own none by default (the shop will sell
// them); the online roster's other players may carry one via the
// `victoryAnim` player field, which is what makes them show up in matches.
//
// drawVictoryAnim(ctx, x, y, headR, color, flagCode, t, type)
//   x, y     : winner head position in CANVAS pixels (already scaled)
//   headR    : head radius in canvas pixels
//   color    : the worm's colour
//   flagCode : the player's flag (flag_plant waves it; pass null to skip)
//   t        : seconds since the victory lap started
//   type     : one of VICTORY_ANIMS

import { flagUrlSquare, isFlagCode } from "../shared/flags";

export const VICTORY_ANIMS = ["flag_plant", "fireworks", "coronation", "akimbo", "trophy", "disco", "champagne"] as const;
export type VictoryAnimType = (typeof VICTORY_ANIMS)[number];

export function isVictoryAnim(v: unknown): v is VictoryAnimType {
  return typeof v === "string" && (VICTORY_ANIMS as readonly string[]).indexOf(v) !== -1;
}


const TAU = Math.PI * 2;
const clamp01 = (v: number) => Math.max(0, Math.min(1, v));
const eo = (v: number) => 1 - Math.pow(1 - v, 3);
const eob = (v: number) => { const c = 1.70158; const u = v - 1; return 1 + (c + 1) * u * u * u + c * u * u; };
// Replays a one-shot timeline: plays through once from 0, then loops the part
// after `start` every `period` seconds (the intro before `start` runs once).
const loopT = (t: number, start: number, period: number) => (t < start ? t : start + ((t - start) % period));
const rnd = (i: number) => { const s = Math.sin(i * 127.1 + 311.7) * 43758.5453; return s - Math.floor(s); };
const CONF = ["#ffcf4d", "#28c4d4", "#ff7ab8", "#7CFC66", "#ffffff"];

// Rectangular (unmasked) flag images, lazily loaded per code. Until an image
// arrives the flag parts simply don't draw for a frame or two.
// The /flags/ assets are CIRCLE-masked icons (pill edges on a banner); the
// /flags-square/ set is the same art unmasked - full square, hard corners,
// symmetric bands. Each flag is rasterized once into an offscreen canvas so
// the wave slicer works from a clean bitmap.
const flagImgCache: Record<string, HTMLImageElement> = {};
const flagRectCache: Record<string, HTMLCanvasElement> = {};
function rectFlag(code: string | null | undefined): HTMLCanvasElement | null {
  if (!code || !isFlagCode(code)) return null;
  const done = flagRectCache[code];
  if (done) return done;
  let img = flagImgCache[code];
  if (!img) {
    img = new Image();
    img.src = flagUrlSquare(code); // unmasked square art: hard corners, symmetric bands
    flagImgCache[code] = img;
  }
  if (!img.complete || img.naturalWidth === 0) return null;
  const c = document.createElement("canvas");
  c.width = 120; c.height = 75; // 8:5, matching the 32x20 banner
  const ctx = c.getContext("2d")!;
  ctx.drawImage(img, 0, 0, c.width, c.height);
  flagRectCache[code] = c;
  return c;
}

function confetti(ctx: CanvasRenderingContext2D, x: number, y: number, t0: number, t: number, n: number, spread: number, u1: number) {
  const u = clamp01((t - t0) / 1.4);
  if (u <= 0 || u >= 1) return;
  for (let k = 0; k < n; k++) {
    const a = rnd(k) * TAU, sp = spread * (0.4 + rnd(k + 7) * 0.9);
    const px = x + Math.cos(a) * sp * eo(u), py = y + Math.sin(a) * sp * eo(u) + 34 * u1 * u * u;
    ctx.save(); ctx.translate(px, py); ctx.rotate(rnd(k + 3) * TAU + u * 6 * (rnd(k) > 0.5 ? 1 : -1));
    ctx.globalAlpha = 1 - u; ctx.fillStyle = CONF[k % CONF.length];
    ctx.fillRect(-2.4 * u1, -1.4 * u1, 4.8 * u1, 2.8 * u1); ctx.restore();
  }
}

function burst(ctx: CanvasRenderingContext2D, bx: number, by: number, u: number, size: number, cols: string[], u1: number) {
  if (u <= 0 || u >= 1) return;
  for (let k = 0; k < 14; k++) {
    const a = (k / 14) * TAU + rnd(k), d = size * (0.35 + rnd(k) * 0.65) * eo(u);
    ctx.globalAlpha = 1 - u; ctx.fillStyle = cols[k % cols.length];
    ctx.beginPath(); ctx.arc(bx + Math.cos(a) * d, by + Math.sin(a) * d + 6 * u1 * u * u, 1.9 * u1 * (1 - u * 0.5), 0, TAU); ctx.fill();
    ctx.globalAlpha = 1;
  }
}

function crown(ctx: CanvasRenderingContext2D, x: number, y: number, u1: number) {
  ctx.save(); ctx.translate(x, y); ctx.scale(u1, u1); ctx.fillStyle = "#ffcf4d";
  ctx.beginPath(); ctx.moveTo(-9, 4); ctx.lineTo(-9, -3); ctx.lineTo(-4.5, 1); ctx.lineTo(0, -5); ctx.lineTo(4.5, 1); ctx.lineTo(9, -3); ctx.lineTo(9, 4); ctx.closePath(); ctx.fill();
  ctx.restore();
}

function hat(ctx: CanvasRenderingContext2D, x: number, y: number, u1: number) {
  ctx.save(); ctx.translate(x, y); ctx.scale(u1, u1);
  ctx.fillStyle = "#8a5a2b"; ctx.beginPath(); ctx.ellipse(0, 0, 11, 3.4, 0, 0, TAU); ctx.fill();
  ctx.fillStyle = "#a06a33"; ctx.fillRect(-5.5, -7, 11, 7);
  ctx.beginPath(); ctx.ellipse(0, -7, 5.5, 2, 0, 0, TAU); ctx.fill();
  ctx.restore();
}

// --- champagne (the 1000-egg celebration) ---------------------------------
// A champagne bottle drawn upright at the origin (+y down), neck opening at
// (0,-29). Golden glass, gold foil cap and collar, cream label with a gold
// band. Rotated by a, scaled by sc.
function bottle(ctx: CanvasRenderingContext2D, x: number, y: number, a: number, sc: number) {
  ctx.save(); ctx.translate(x, y); ctx.rotate(a); ctx.scale(sc, sc);
  const g = ctx.createLinearGradient(-7, 0, 7, 0);
  g.addColorStop(0, "#6b4a0c"); g.addColorStop(0.35, "#ffe27a"); g.addColorStop(0.55, "#ffcf4d"); g.addColorStop(1, "#7a5310");
  ctx.fillStyle = g; ctx.strokeStyle = "#3a2a06"; ctx.lineWidth = 0.9; ctx.lineJoin = "round";
  ctx.beginPath(); ctx.moveTo(-7, 16); ctx.lineTo(-7, -6); ctx.quadraticCurveTo(-7, -14, -3, -17); ctx.lineTo(-3, -28); ctx.lineTo(3, -28); ctx.lineTo(3, -17);
  ctx.quadraticCurveTo(7, -14, 7, -6); ctx.lineTo(7, 16); ctx.closePath(); ctx.fill(); ctx.stroke();
  // gold foil: cap + collar down to the shoulder
  const f = ctx.createLinearGradient(-4, 0, 4, 0);
  f.addColorStop(0, "#b07d09"); f.addColorStop(0.45, "#ffe27a"); f.addColorStop(1, "#8a5f14");
  ctx.fillStyle = f;
  ctx.beginPath(); ctx.moveTo(-3.6, -29); ctx.lineTo(3.6, -29); ctx.lineTo(3.4, -20); ctx.quadraticCurveTo(6, -15, 6.2, -12); ctx.lineTo(-6.2, -12); ctx.quadraticCurveTo(-6, -15, -3.4, -20); ctx.closePath(); ctx.fill();
  ctx.fillStyle = "#c8322b"; ctx.fillRect(-6.2, -13, 12.4, 1.4);
  // label
  ctx.fillStyle = "#f6efd8"; ctx.fillRect(-5.4, -3, 10.8, 11);
  ctx.fillStyle = "#c9a15a"; ctx.fillRect(-5.4, 1.2, 10.8, 1.6);
  ctx.fillStyle = "#8a5f14"; ctx.beginPath(); ctx.arc(0, -0.2, 1.3, 0, TAU); ctx.fill();
  // glass highlight
  ctx.fillStyle = "rgba(255,255,255,0.22)"; ctx.fillRect(-5.6, -9, 1.5, 24);
  ctx.restore();
}
// Where the neck opening sits for a bottle at (bx,by) rotated a, scaled sc,
// and the direction it sprays along (the bottle's own axis).
function bottleTip(bx: number, by: number, a: number, sc: number) {
  return { x: bx + Math.sin(a) * 29 * sc, y: by - Math.cos(a) * 29 * sc, axis: a - Math.PI / 2 };
}
// A champagne jet from (x,y) along angle a, power p in [0,1]. Every droplet
// is a projectile: launched from the neck at time t-age with its own speed
// (V, lightly pulsing) and angle (a plus a little spread), falling under
// gravity G, drawn as a streak along its velocity and dying with a splash
// ring when it reaches the floor. The first 0.32s of flight is also drawn as
// a tapered ribbon along the mean trajectory: coherent out of the bottle,
// breaking into droplets after. t0 is when the jet started (nothing older).
function jet(ctx: CanvasRenderingContext2D, x: number, y: number, a: number, p: number, t: number, t0: number, floor: number, seed: number, u1: number) {
  if (p <= 0) return;
  const V = 260 * u1, G = 240 * u1, SPREAD = 0.14, LIFE = 1.5, N = 150, CORE = 0.32;
  ctx.save(); ctx.lineCap = "round";
  const cvx = Math.cos(a) * V, cvy = Math.sin(a) * V;
  for (let pass = 0; pass < 2; pass++) {
    const S = 14;
    for (let i = 0; i < S; i++) {
      const a0 = (i / S) * CORE, a1 = ((i + 1) / S) * CORE, f = 1 - i / S;
      ctx.lineWidth = Math.max(0.4, (pass ? 2.4 : 5.2) * p * u1 * (1 - (i / S) * 0.75));
      ctx.strokeStyle = pass ? `rgba(255,246,210,${0.7 * f})` : `rgba(255,214,102,${0.45 * f})`;
      ctx.beginPath(); ctx.moveTo(x + cvx * a0, y + cvy * a0 + 0.5 * G * a0 * a0); ctx.lineTo(x + cvx * a1, y + cvy * a1 + 0.5 * G * a1 * a1); ctx.stroke();
    }
  }
  const since = t - t0;
  for (let k = 0; k < N; k++) {
    const life = LIFE * (0.8 + 0.4 * rnd(seed + k * 7)), age = (((t / life) + rnd(seed + k * 7 + 2)) % 1) * life;
    if (age > since) continue;
    const lt = t - age, v = V * (1 + 0.08 * Math.sin(lt * 9 + seed)) * (0.85 + 0.3 * rnd(seed + k * 3));
    const la = a + (rnd(seed + k * 3 + 1) - 0.5) * SPREAD, vx = Math.cos(la) * v, vy0 = Math.sin(la) * v;
    const px = x + vx * age, py = y + vy0 * age + 0.5 * G * age * age, vy = vy0 + G * age;
    if (py > floor) {
      const d = (py - floor) / (Math.abs(vy) + 1);
      if (d < 0.12) {
        ctx.globalAlpha = (1 - d / 0.12) * 0.7 * p; ctx.strokeStyle = "#fff6d2"; ctx.lineWidth = 0.8 * u1;
        ctx.beginPath(); ctx.ellipse(px, floor, (2 + d * 30) * u1, (0.8 + d * 8) * u1, 0, 0, TAU); ctx.stroke();
      }
      continue;
    }
    const u = age / life, sp = Math.hypot(vx, vy) || 1, len = Math.min(7 * u1, sp * 0.045) * (1 - u * 0.5);
    ctx.globalAlpha = Math.min(1, (1 - u) * 1.4) * p * 0.85;
    ctx.strokeStyle = k % 3 ? "#fff6d2" : "#ffe27a"; ctx.lineWidth = (0.7 + rnd(seed + k * 7 + 5) * 1.3) * (1 - u * 0.5) * u1;
    ctx.beginPath(); ctx.moveTo(px, py); ctx.lineTo(px - vx / sp * len, py - vy / sp * len); ctx.stroke();
  }
  ctx.globalAlpha = 1; ctx.restore();
}
function mist(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, a: number) {
  const g = ctx.createRadialGradient(x, y, 0, x, y, r);
  g.addColorStop(0, `rgba(255,240,200,${a})`); g.addColorStop(1, "rgba(255,240,200,0)");
  ctx.fillStyle = g; ctx.fillRect(x - r, y - r, r * 2, r * 2);
}

// The disco ball's wire: solid on its lower half, fading to nothing over the
// upper half so it doesn't look like it hangs from a fixed point in mid-air.
function wireStroke(ctx: CanvasRenderingContext2D, yBottom: number, yTop: number): CanvasGradient {
  const g = ctx.createLinearGradient(0, yBottom, 0, yTop);
  g.addColorStop(0, "#8995b3"); g.addColorStop(0.5, "#8995b3"); g.addColorStop(1, "rgba(137,149,179,0)");
  return g;
}

function waveFlag(ctx: CanvasRenderingContext2D, img: HTMLCanvasElement, x: number, y: number, w: number, h: number, t: number, amp: number) {
  const iw = img.width, ih = img.height;
  ctx.save(); ctx.translate(x, y);
  const step = Math.max(1.5, w / 15);
  for (let sx = 0; sx < w; sx += step) {
    const wob = Math.sin(t * 7 + (sx / w) * 4.8) * amp * (sx / w);
    ctx.drawImage(img, (sx / w) * iw, 0, (step / w) * iw, ih, sx, wob, step + 0.4, h);
  }
  ctx.restore();
}

// Fireworks: shells launched from the worm at irregular (but deterministic per
// lap) intervals; each rocket climbs to its own point and the burst stays PUT
// there while the worm drives on. That needs a memory of where past shells
// exploded, so unlike the other celebrations this one keeps a tiny module
// scope state. Only one worm celebrates at a time (the lap event carries a
// single winner), and a lap restart is detected by time jumping backwards.
interface Shell {
  dx: number; dy: number;   // flight vector in the HEAD's frame: the rocket rides
                            // along with the worm, climbing out to this offset
  ex?: number; ey?: number; // world point frozen at detonation; the burst stays here
  t0: number;               // launch time (lap clock)
  tx: number;               // explosion time
  size: number;
  big: boolean;             // giant multi-colour shell vs a quick pop
  seed: number;
}
export function createFireworksState() {
  return { lastT: -1, nextLaunch: 0, count: 0, hx: 0, hy: 0, heading: -Math.PI / 2, shells: [] as Shell[] };
}
type FireworksState = ReturnType<typeof createFireworksState>;
// In a match only one worm celebrates at a time, so live rendering shares this
// default state; the shop's previews can run several fireworks at once (a card
// grid plus the confirm modal), so each passes its own state via opts.
const fw = createFireworksState();

// Per-caller tuning for the fireworks celebration (the other anims ignore it).
export interface VictoryFxOpts {
  fwState?: FireworksState;  // per-canvas state (see above)
  fwHeading?: number;        // fixed heading for worms that don't move (previews)
  fwDistScale?: number;      // scales rocket travel (tight preview tiles use 0.5)
  // The celebration's size factor, decoupled from headR. In the game it follows
  // the head (u1 = headR / 7 × 1.15). A screen that plays the celebration around
  // something bigger than a worm head (the results screen's avatar disc) passes
  // the disc's radius as headR, so crowns, hats and bottles hover CLEAR of the
  // disc, and a sane size here so they don't grow with it.
  scale?: number;
}
// The size factor for a head radius, unless the caller pins one (opts.scale).
function sizeFor(headR: number, opts?: VictoryFxOpts): number {
  return opts?.scale ?? Math.max(0.5, (headR / 7) * 1.15);
}

function fireworksFrame(ctx: CanvasRenderingContext2D, x: number, y: number, color: string, t: number, u1: number, launching: boolean, opts?: VictoryFxOpts) {
  const S = opts?.fwState ?? fw;
  if (t < S.lastT - 0.25) { S.shells.length = 0; S.nextLaunch = 0; S.count = 0; S.hx = x; S.hy = y; } // new lap
  S.lastT = t;
  // Track the worm's heading from how the head moves between frames (the draw
  // call doesn't carry direction), so rockets can fire ahead of the worm.
  const mdx = x - S.hx, mdy = y - S.hy;
  if (mdx * mdx + mdy * mdy > 0.01) S.heading = Math.atan2(mdy, mdx);
  S.hx = x; S.hy = y;
  if (launching && t >= S.nextLaunch && S.shells.length < 8) {
    const i = S.count++;
    const big = rnd(i * 3.7 + 11) < 0.3;
    // fan across a 120 degree cone AHEAD of the worm (heading +-60 degrees), never backwards
    const a = (opts?.fwHeading ?? S.heading) + (rnd(i * 5.1 + 2) - 0.5) * (TAU / 3);
    const d = (60 + rnd(i + 31) * 90) * u1 * (opts?.fwDistScale ?? 1);
    S.shells.push({
      dx: Math.cos(a) * d,
      dy: Math.sin(a) * d,
      t0: t,
      // double the travel in not-quite-double the time: a touch faster in flight
      tx: t + 0.45 + rnd(i + 13) * 0.3,
      size: (big ? 30 : 23.4 + rnd(i + 23) * 6.6) * u1,
      big,
      seed: i,
    });
    // uneven cadence: sometimes a quick follow-up, sometimes a breather
    S.nextLaunch = t + ((big ? 0.8 : 0.25) + rnd(i * 9.7 + 5) * 1.1) / 1.66;
  }
  for (const s of S.shells) {
    if (t < s.tx) {
      // the projectile rides in the head's frame: launched from the (moving)
      // head, flying out along its vector at a constant pace, keeping up with
      // the worm. Just a glowing ball, no trail.
      const p = clamp01((t - s.t0) / (s.tx - s.t0));
      ctx.fillStyle = "#fff6d2"; ctx.beginPath(); ctx.arc(x + s.dx * p, y + s.dy * p, 2 * u1, 0, TAU); ctx.fill();
    } else if (s.ex === undefined) {
      // first frame past detonation: freeze the burst where the rocket is NOW
      s.ex = x + s.dx; s.ey = y + s.dy;
    }
    if (t < s.tx || s.ex === undefined || s.ey === undefined) continue;
    if (s.big) {
      const u = clamp01((t - s.tx) / 1.3);
      if (u < 1) {
        for (let k = 0; k < 26; k++) {
          const a = (k / 26) * TAU + rnd(k + s.seed) * 0.2, d = 2.6 * s.size * (0.5 + rnd(k + s.seed) * 0.5) * eo(u);
          ctx.globalAlpha = 1 - u; ctx.fillStyle = CONF[(k + s.seed) % CONF.length];
          ctx.beginPath(); ctx.arc(s.ex + Math.cos(a) * d, s.ey + Math.sin(a) * d * 0.8 + 16 * u1 * u * u, 2.2 * u1 * (1 - u * 0.4), 0, TAU); ctx.fill();
          ctx.globalAlpha = 1;
        }
        ctx.globalAlpha = (1 - u) * 0.5; ctx.fillStyle = "#fff";
        ctx.beginPath(); ctx.arc(s.ex, s.ey, 6 * u1 * (1 - u), 0, TAU); ctx.fill(); ctx.globalAlpha = 1;
      }
    } else {
      burst(ctx, s.ex, s.ey, clamp01((t - s.tx) / 0.55), s.size, rnd(s.seed + 77) < 0.5 ? [color, "#fff"] : ["#fff", color], u1);
    }
  }
  for (let i = S.shells.length - 1; i >= 0; i--) {
    const s = S.shells[i];
    if (t > s.tx + (s.big ? 1.3 : 0.55)) S.shells.splice(i, 1);
  }
}

export function drawVictoryAnim(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  headR: number,
  color: string,
  flagCode: string | null | undefined,
  t: number,
  type: VictoryAnimType,
  opts?: VictoryFxOpts,
): void {
  // Runs for the whole lap, however long: ambient parts (waving flag, disco,
  // trophy shine, champagne) simply keep going, and the one-shot parts
  // (bursts, confetti, shots) replay on a cycle so the celebration never goes
  // quiet before the next round replaces the gameState.
  if (t < 0) return;
  const u1 = sizeFor(headR, opts); // authored at head radius 7, then a global 15% size-up vs the worm
  ctx.save();
  ctx.lineCap = "round";
  switch (type) {
    case "flag_plant": { // 1D: a small arm holds the pole beside the worm (gunslinger-style); golden finial, gusty wave, glow, confetti burst
      const up = clamp01(t / 0.5), pole = 46 * u1 * eob(up);
      // The hand grips the pole beside the head, not on top of it.
      const hx = x + 17 * u1, hy = y - 5 * u1, py0 = hy + 6 * u1;
      // pole first, then the arm + hand OVER it so the grip reads as holding.
      ctx.strokeStyle = "#e8edf6"; ctx.lineWidth = 2.4 * u1;
      ctx.beginPath(); ctx.moveTo(hx, py0); ctx.lineTo(hx, py0 - pole); ctx.stroke();
      ctx.strokeStyle = "#FFB74D"; ctx.lineWidth = 3 * u1;
      ctx.beginPath(); ctx.moveTo(x + 5 * u1, y - 1 * u1); ctx.lineTo(hx, hy); ctx.stroke();
      ctx.fillStyle = "#FFB74D"; ctx.beginPath(); ctx.arc(hx, hy, 2.2 * u1, 0, TAU); ctx.fill();
      if (up > 0.9) { ctx.fillStyle = "#ffcf4d"; ctx.beginPath(); ctx.arc(hx, py0 - pole, 2.6 * u1, 0, TAU); ctx.fill(); }
      const unf = clamp01((t - 0.45) / 0.5);
      const gust = 1 + 1.6 * Math.pow(Math.max(0, Math.sin(t * 1.1 - 1)), 3);
      const img = rectFlag(flagCode);
      if (unf > 0 && img) {
        // The flag starts at the pole's right edge (kisses the bar, never covers it).
        const fx = hx + 1.2 * u1;
        ctx.save(); ctx.beginPath(); ctx.rect(fx, py0 - pole + 2 * u1, 32 * u1 * eo(unf), 20 * u1); ctx.clip();
        waveFlag(ctx, img, fx, py0 - pole + 2 * u1, 32 * u1, 20 * u1, t, 1.4 * u1 * gust);
        ctx.restore();
      }
      confetti(ctx, hx, py0 - pole + 6 * u1, 0.5, loopT(t, 0.5, 3.2), 26, 46 * u1, u1);
      break;
    }
    case "fireworks": { // 3C: random volleys fired from the worm; bursts stay where they explode
      fireworksFrame(ctx, x, y, color, t, u1, true, opts);
      break;
    }
    case "coronation": { // 4C: light beam, slow crown descent, confetti landing
      const bu = clamp01(t / 0.3);
      const g = ctx.createLinearGradient(0, y - 110 * u1, 0, y);
      g.addColorStop(0, `rgba(255,240,190,${0.26 * bu})`); g.addColorStop(1, "rgba(255,240,190,0)");
      ctx.fillStyle = g;
      ctx.beginPath(); ctx.moveTo(x - 9 * u1, y - 110 * u1); ctx.lineTo(x + 9 * u1, y - 110 * u1); ctx.lineTo(x + 26 * u1, y); ctx.lineTo(x - 26 * u1, y); ctx.closePath(); ctx.fill();
      // descends from high in the beam, taking its time
      const du = clamp01((t - 0.25) / 1.4), dy = -95 * u1 * (1 - eo(du));
      crown(ctx, x, y - headR - 16 * u1 + dy, 1.32 * u1); // larger crown, hovering clear of the head
      confetti(ctx, x, y - headR - 19 * u1, 1.65, loopT(t, 1.65, 3.0), 18, 34 * u1, u1); // pops as the crown lands
      for (let k = 0; k < 6; k++) {
        const u = (t * 0.6 + rnd(k)) % 1;
        ctx.globalAlpha = (1 - u) * 0.6 * bu; ctx.fillStyle = "#fff";
        ctx.beginPath(); ctx.arc(x - 14 * u1 + rnd(k + 3) * 28 * u1, y - 10 * u1 - u * 56 * u1, 1.1 * u1, 0, TAU); ctx.fill();
        ctx.globalAlpha = 1;
      }
      break;
    }
    case "akimbo": { // 9B: two revolvers on wide arms, tracers, flashes, brass
      // the whole rig scaled up a touch around the head
      ctx.save(); ctx.translate(x, y); ctx.scale(1.15, 1.15); ctx.translate(-x, -y);
      hat(ctx, x, y - headR - 4 * u1, 1.15 * u1); // net 1.32 with the scene scale, matching the crown
      const grips: [number, number, number][] = [[x + 22 * u1, y - 10 * u1, 1], [x - 22 * u1, y - 10 * u1, -1]];
      const tips: [number, number][] = [[x + 31 * u1, y - 26 * u1], [x - 31 * u1, y - 26 * u1]];
      // endless alternating fire, no reload pause: render every shot whose
      // effects (flash, tracer, pop, smoke, brass) can still be live
      const shots: { side: number; st: number }[] = [];
      for (let j = Math.max(0, Math.ceil((t - 2.0) / 0.26)); 0.5 + j * 0.26 <= t; j++) {
        shots.push({ side: j % 2, st: 0.5 + j * 0.26 });
      }
      const ta = t;
      const recoil = [0, 0];
      shots.forEach((sh) => { const u = clamp01((ta - sh.st) / 0.3); if (u > 0 && u < 1) recoil[sh.side] = Math.max(recoil[sh.side], 1 - u); });
      grips.forEach(([gx, gy, dir], side) => {
        ctx.strokeStyle = "#FFB74D"; ctx.lineWidth = 3 * u1;
        ctx.beginPath(); ctx.moveTo(x + dir * 6 * u1, y - 1 * u1); ctx.lineTo(gx - dir * 2 * u1, gy + 3 * u1); ctx.stroke();
        ctx.save(); ctx.translate(gx, gy); ctx.scale(dir * u1, u1); ctx.rotate(-1.05 - recoil[side] * 0.4);
        ctx.fillStyle = "#39415c"; ctx.fillRect(0, -2.6, 15, 5.2); ctx.fillRect(-1, 0, 5, 9);
        ctx.fillStyle = "#212842"; ctx.fillRect(4, -3.6, 5, 7.2);
        ctx.restore();
      });
      shots.forEach((sh) => {
        const [tx, ty] = tips[sh.side]; const dir = sh.side === 0 ? 1 : -1;
        const fu = clamp01((ta - sh.st) / 0.3);
        if (fu > 0 && fu < 1) {
          ctx.save(); ctx.translate(tx, ty); ctx.rotate(rnd(sh.st * 10) * TAU);
          ctx.fillStyle = `rgba(255,224,130,${1 - fu})`;
          for (let k = 0; k < 6; k++) {
            ctx.rotate(TAU / 6);
            ctx.beginPath(); ctx.moveTo(0, 0); ctx.lineTo(3.2 * u1, 10 * u1 * (1 - fu * 0.4)); ctx.lineTo(-3.2 * u1, 10 * u1 * (1 - fu * 0.4)); ctx.closePath(); ctx.fill();
          }
          ctx.restore();
        }
        const tu = clamp01((ta - sh.st) / 0.22);
        if (tu > 0 && tu < 1) {
          ctx.strokeStyle = `rgba(255,255,255,${0.95 * (1 - tu)})`; ctx.lineWidth = 2.2 * u1;
          const len = 30 * u1, ox = dir * 0.45, oy = -1;
          ctx.beginPath(); ctx.moveTo(tx + ox * len * tu, ty + oy * len * tu);
          ctx.lineTo(tx + ox * len * (tu + 0.35), ty + oy * len * (tu + 0.35)); ctx.stroke();
        }
        // the bullet doesn't just vanish: a small firework pop where the tracer ends
        const pu = clamp01((ta - sh.st - 0.22) / 0.45);
        if (pu > 0 && pu < 1) burst(ctx, tx + dir * 0.45 * 40.5 * u1, ty - 40.5 * u1, pu, 8 * u1, ["#ffcf4d", "#fff", color], u1);
        const su = clamp01((ta - sh.st - 0.12) / 0.8);
        if (su > 0 && su < 1) {
          for (let k = 0; k < 2; k++) {
            ctx.strokeStyle = `rgba(203,213,225,${0.45 * (1 - su)})`; ctx.lineWidth = 1.6 * u1;
            ctx.beginPath(); ctx.arc(tx + dir * 3 * u1 * su + k * 3 * u1 * dir, ty - 8 * u1 - su * 16 * u1 - k * 5 * u1, 2.5 * u1 + su * 4 * u1, 0, TAU); ctx.stroke();
          }
        }
        const cu = clamp01((ta - sh.st - 0.05) / 0.75);
        if (cu > 0 && cu < 1) {
          const cx2 = tx - dir * 10 * u1 - dir * 14 * u1 * cu;
          const cy2 = ty + 6 * u1 - 24 * u1 * Math.sin(Math.PI * Math.min(1, cu * 1.1)) + 44 * u1 * cu * cu;
          ctx.save(); ctx.translate(cx2, cy2); ctx.rotate(cu * 9 * dir);
          ctx.fillStyle = `rgba(222,178,80,${1 - cu * 0.7})`; ctx.fillRect(-2 * u1, -3.2 * u1, 4 * u1, 6.4 * u1);
          ctx.fillStyle = `rgba(255,240,190,${0.9 * (1 - cu)})`; ctx.fillRect(-2 * u1, -3.2 * u1, 4 * u1, 1.4 * u1);
          ctx.restore();
        }
      });
      ctx.restore();
      break;
    }
    case "trophy": { // 10D: rising cup + shine + sparks + rotating sunburst
      const rise = eob(clamp01(t / 0.7)), ty = y - 16 * u1 - 33 * u1 * rise; // hovers at -49, clear of the head
      ctx.save(); ctx.translate(x, ty - 2 * u1); ctx.rotate(t * 0.5);
      for (let k = 0; k < 10; k++) {
        ctx.rotate(TAU / 10);
        const grad = ctx.createLinearGradient(0, 0, 0, -58 * u1);
        grad.addColorStop(0, `rgba(255,207,77,${0.22 * rise})`); grad.addColorStop(1, "rgba(255,207,77,0)");
        ctx.fillStyle = grad;
        ctx.beginPath(); ctx.moveTo(0, 0); ctx.lineTo(6.5 * u1, -58 * u1); ctx.lineTo(-6.5 * u1, -58 * u1); ctx.closePath(); ctx.fill();
      }
      ctx.restore();
      ctx.save(); ctx.translate(x, ty); ctx.scale(1.3 * u1, 1.3 * u1); // the cup reads better a third larger
      ctx.fillStyle = "#ffcf4d"; ctx.strokeStyle = "#b07d09"; ctx.lineWidth = 1;
      ctx.beginPath(); ctx.moveTo(-8, -8); ctx.quadraticCurveTo(-8, 4, 0, 5); ctx.quadraticCurveTo(8, 4, 8, -8); ctx.closePath(); ctx.fill();
      ctx.beginPath(); ctx.arc(-9.5, -4, 4, Math.PI * 0.5, Math.PI * 1.5); ctx.stroke();
      ctx.beginPath(); ctx.arc(9.5, -4, 4, -Math.PI * 0.5, Math.PI * 0.5); ctx.stroke();
      ctx.fillRect(-2, 5, 4, 4); ctx.fillRect(-6, 9, 12, 3);
      const sh = ((t * 1.1) % 1.6) / 1.6 * 30 - 12;
      ctx.beginPath(); ctx.moveTo(-8, -8); ctx.quadraticCurveTo(-8, 4, 0, 5); ctx.quadraticCurveTo(8, 4, 8, -8); ctx.closePath(); ctx.clip();
      ctx.fillStyle = "rgba(255,255,255,.35)"; ctx.fillRect(sh - 2, -10, 4, 20);
      ctx.restore();
      for (let k = 0; k < 10; k++) {
        const u = (t * 0.8 + rnd(k)) % 1;
        ctx.globalAlpha = (1 - u) * 0.9; ctx.fillStyle = k % 2 ? "#ffcf4d" : "#fff";
        const a = rnd(k + 6) * TAU;
        ctx.beginPath(); ctx.arc(x + Math.cos(a) * (15 * u1 + rnd(k) * 20 * u1), ty - 4 * u1 - u * 24 * u1, 1.6 * u1, 0, TAU); ctx.fill();
        ctx.globalAlpha = 1;
      }
      break;
    }
    case "champagne": { // Crossfire Dome: crown drops, two bottles rise wide and spray steep arcs that meet overhead
      // the crown eases down onto the head while the bottles come up
      const cu = clamp01((t - 0.15) / 0.9), cy = -70 * u1 * (1 - eo(cu));
      crown(ctx, x, y - headR - 16 * u1 + cy, 1.32 * u1); // same hover gap and size as the coronation crown
      if (cu >= 1) {
        for (let k = 0; k < 5; k++) {
          const u = (t * 0.9 + rnd(k + 40)) % 1;
          ctx.globalAlpha = (1 - u) * 0.9; ctx.strokeStyle = "#fff"; ctx.lineWidth = 0.7 * u1;
          const sx = x + (rnd(k + 41) - 0.5) * 22 * u1, sy = y - headR - 17 * u1 - u * 10 * u1, sr = 2 * u1 * Math.sin(u * Math.PI);
          ctx.beginPath(); ctx.moveTo(sx - sr, sy); ctx.lineTo(sx + sr, sy); ctx.moveTo(sx, sy - sr); ctx.lineTo(sx, sy + sr); ctx.stroke();
          ctx.globalAlpha = 1;
        }
      }
      const T0 = 1.0, floor = y + 26 * u1;
      ([[-1, 0], [1, 0.1]] as [number, number][]).forEach(([sd, delay], i) => {
        const bx = x + sd * 66 * u1, by = y + 10 * u1;
        const up = eob(clamp01((t - 0.3 - delay) / 0.6)), tilt = -sd * 0.5 * up;
        const t0 = T0 + delay, pop = t > t0, p = pop ? clamp01((t - t0) / 0.3) * (1 - 0.12 * Math.sin(t * 8 + i)) : 0;
        const tip = bottleTip(bx, by, tilt, 0.85 * u1);
        if (pop) {
          jet(ctx, tip.x, tip.y, tip.axis, p, t, t0, floor, 17 + i * 13, u1);
          if (t - t0 < 0.2) {
            const fg = ctx.createRadialGradient(tip.x, tip.y, 0, tip.x, tip.y, 18 * u1);
            fg.addColorStop(0, `rgba(255,255,255,${1 - (t - t0) * 5})`); fg.addColorStop(1, "rgba(255,255,255,0)");
            ctx.fillStyle = fg; ctx.fillRect(tip.x - 18 * u1, tip.y - 18 * u1, 36 * u1, 36 * u1);
          }
        }
        bottle(ctx, bx, by, tilt, 0.85 * u1);
      });
      // a little mist where the two arcs meet
      if (t > T0 + 0.5) mist(ctx, x, y - 70 * u1, 34 * u1, 0.1);
      break;
    }
    case "disco": { // 11D: ball + sweeping beams + strobing dots + beat-glow
      const drop = eob(clamp01(t / 0.6)), by = y - 80 * u1 + 30 * u1 * drop;
      (
        [["rgba(40,196,212,", 0], ["rgba(255,122,184,", Math.PI / 2]] as [string, number][]
      ).forEach(([col, ph]) => {
        const a = Math.sin(t * 1.6 + ph) * 0.9;
        ctx.save(); ctx.translate(x, by); ctx.rotate(a);
        const g = ctx.createLinearGradient(0, 0, 0, 120 * u1);
        g.addColorStop(0, col + "0.28)"); g.addColorStop(1, col + "0)");
        ctx.fillStyle = g;
        ctx.beginPath(); ctx.moveTo(0, 0); ctx.lineTo(16 * u1, 120 * u1); ctx.lineTo(-16 * u1, 120 * u1); ctx.closePath(); ctx.fill();
        ctx.restore();
      });
      // the ball on its wire (the wire runs up out of frame)
      ctx.strokeStyle = wireStroke(ctx, by - 9 * u1, by - 100 * u1); ctx.lineWidth = 1.2 * u1;
      ctx.beginPath(); ctx.moveTo(x, by - 100 * u1); ctx.lineTo(x, by - 9 * u1); ctx.stroke();
      ctx.save(); ctx.translate(x, by); ctx.rotate(t * 1.4);
      ctx.fillStyle = "#c3cedd"; ctx.beginPath(); ctx.arc(0, 0, 9 * u1, 0, TAU); ctx.fill();
      ctx.strokeStyle = "rgba(255,255,255,.55)"; ctx.lineWidth = 0.7 * u1;
      for (let k = -2; k <= 2; k++) { ctx.beginPath(); ctx.ellipse(0, 0, 9 * u1, Math.abs(k) * 3 * u1 + 0.01, 0, 0, TAU); ctx.stroke(); }
      ctx.beginPath(); ctx.moveTo(0, -9 * u1); ctx.lineTo(0, 9 * u1); ctx.stroke(); ctx.restore();
      for (let k = 0; k < 12; k++) {
        const a = t * 2 + k * (TAU / 12), rr = 34 * u1 + Math.sin(t * 3 + k) * 10 * u1;
        ctx.globalAlpha = 0.5 + 0.5 * Math.sin(t * 8 + k); ctx.fillStyle = CONF[k % CONF.length];
        ctx.beginPath(); ctx.arc(x + Math.cos(a) * rr, y + Math.sin(a) * rr * 0.55, 2.4 * u1, 0, TAU); ctx.fill();
        ctx.globalAlpha = 1;
      }
      // (no halo around the head: it fights with cosmetic worm heads)
      break;
    }
  }
  ctx.restore();
}

// ---------------------------------------------------------------------------
// Crash sequences: what the celebration does when the winner dies mid-lap.
// drawVictoryCrash(ctx, x, y, headR, color, flagCode, tc, type)
//   tc     : seconds since the crash. Nothing touches the head itself (heads
//            are cosmetics of their own); only the celebration's props react.
//   tAtCrash: lap time at the moment of the crash, so props that were moving
//            with the lap clock (the disco dots) start exactly where they were.

// A thrown prop: launched from (x0,y0) with velocity (vx,vy), gravity g,
// bouncing on the floor at fy with restitution `rest`, spinning at `spin`.
// Integrated from 0 to tc so it is a pure function of time (no state).
function toss(tc: number, x0: number, y0: number, vx: number, vy: number, g: number, fy: number, rest: number, spin: number) {
  let x = x0, y = y0, vy2 = vy, rot = 0, w = spin, t = 0;
  const dt = 1 / 120;
  while (t < tc) {
    x += vx * dt; vy2 += g * dt; y += vy2 * dt; rot += w * dt;
    if (y > fy) { y = fy; vy2 = -vy2 * rest; vx *= 0.7; w *= 0.6; if (Math.abs(vy2) < 8) { vy2 = 0; w *= 0.9; } }
    t += dt;
  }
  return { x, y, rot, settled: y >= fy - 0.5 && Math.abs(vy2) < 1 };
}
function puff(ctx: CanvasRenderingContext2D, x: number, y: number, tc: number, size: number, col = "200,210,225") {
  if (tc < 0 || tc > 1) return;
  for (let k = 0; k < 5; k++) {
    const a = rnd(k + 900) * TAU, d = size * tc * (0.5 + rnd(k + 901) * 0.6);
    ctx.globalAlpha = (1 - tc) * 0.5; ctx.fillStyle = `rgba(${col},1)`;
    ctx.beginPath(); ctx.arc(x + Math.cos(a) * d, y + Math.sin(a) * d * 0.6 - tc * size * 0.4, size * 0.35 * (0.5 + tc), 0, TAU); ctx.fill();
  }
  ctx.globalAlpha = 1;
}
function revolver(ctx: CanvasRenderingContext2D) {
  ctx.fillStyle = "#39415c"; ctx.fillRect(0, -2.6, 15, 5.2); ctx.fillRect(-1, 0, 5, 9);
  ctx.fillStyle = "#212842"; ctx.fillRect(4, -3.6, 5, 7.2);
}
function trophyCup(ctx: CanvasRenderingContext2D) {
  ctx.fillStyle = "#ffcf4d"; ctx.strokeStyle = "#b07d09"; ctx.lineWidth = 1;
  ctx.beginPath(); ctx.moveTo(-8, -8); ctx.quadraticCurveTo(-8, 4, 0, 5); ctx.quadraticCurveTo(8, 4, 8, -8); ctx.closePath(); ctx.fill();
  ctx.beginPath(); ctx.arc(-9.5, -4, 4, Math.PI * 0.5, Math.PI * 1.5); ctx.stroke();
  ctx.beginPath(); ctx.arc(9.5, -4, 4, -Math.PI * 0.5, Math.PI * 0.5); ctx.stroke();
}
function trophyBase(ctx: CanvasRenderingContext2D) {
  ctx.fillStyle = "#ffcf4d"; ctx.fillRect(-2, 5, 4, 4); ctx.fillRect(-6, 9, 12, 3);
}
function dribble(ctx: CanvasRenderingContext2D, x: number, y: number, a: number, tc: number, seed: number, u1: number) {
  if (tc < 0.1) return;
  for (let k = 0; k < 8; k++) {
    const u = ((tc * 1.2) + rnd(seed + k)) % 1;
    const px = x + Math.cos(a) * 6 * u1 * u, py = y + Math.sin(a) * 6 * u1 * u + 40 * u1 * u * u;
    ctx.globalAlpha = (1 - u) * 0.8 * Math.max(0, 1 - tc / 2.5); ctx.fillStyle = "#fff6d2";
    ctx.beginPath(); ctx.arc(px, py, (1 + rnd(seed + k + 5)) * u1, 0, TAU); ctx.fill();
  }
  ctx.globalAlpha = 1;
}

export function drawVictoryCrash(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  headR: number,
  color: string,
  flagCode: string | null | undefined,
  tc: number,
  type: VictoryAnimType,
  tAtCrash = 0,
  opts?: VictoryFxOpts,
): void {
  if (tc < 0) return;
  const u1 = sizeFor(headR, opts); // same global size-up as drawVictoryAnim
  const floor = y + 26 * u1;
  ctx.save();
  ctx.lineCap = "round";
  switch (type) {
    case "flag_plant": { // the hand-held pole topples with the flag still on it; finial pings off
      // Matches the celebration's arm-held pole: the topple pivots at the hand.
      const hx = x + 17 * u1, py0 = y + 1 * u1;
      const ang = Math.min(Math.PI / 2, eo(clamp01(tc / 0.7)) * (Math.PI / 2) * 1.05) + (tc > 0.7 ? Math.sin((tc - 0.7) * 20) * 0.04 * Math.max(0, 1 - (tc - 0.7) * 3) : 0);
      ctx.save(); ctx.translate(hx, py0); ctx.rotate(ang);
      ctx.strokeStyle = "#e8edf6"; ctx.lineWidth = 2.4 * u1;
      ctx.beginPath(); ctx.moveTo(0, 0); ctx.lineTo(0, -46 * u1); ctx.stroke();
      const img = rectFlag(flagCode);
      if (img) {
        ctx.translate(1.2 * u1, -44 * u1); ctx.rotate(-ang * 0.9 + Math.sin(tc * 7) * 0.08 * Math.max(0, 1 - tc));
        const droop = eo(clamp01(tc / 0.9));
        ctx.scale(1, 1 - droop * 0.25); ctx.drawImage(img, 0, 0, 32 * u1, 20 * u1);
      }
      ctx.restore();
      // the arm loses its grip and slumps back toward the body
      const slump = eo(clamp01(tc / 0.5));
      ctx.strokeStyle = "#FFB74D"; ctx.lineWidth = 3 * u1;
      ctx.beginPath(); ctx.moveTo(x + 5 * u1, y - 1 * u1); ctx.lineTo(hx - 4 * u1 * slump, y - 5 * u1 + 7 * u1 * slump); ctx.stroke();
      ctx.fillStyle = "#FFB74D"; ctx.beginPath(); ctx.arc(hx - 4 * u1 * slump, y - 5 * u1 + 7 * u1 * slump, 2.2 * u1, 0, TAU); ctx.fill();
      const f = toss(tc, hx, py0 - 46 * u1, 60 * u1, -90 * u1, 300 * u1, floor, 0.5, 14);
      ctx.fillStyle = "#ffcf4d"; ctx.beginPath(); ctx.arc(f.x, f.y, 2.6 * u1, 0, TAU); ctx.fill();
      // confetti already in the air finishes falling; no new bursts
      confetti(ctx, hx, py0 - 40 * u1, 0.5, loopT(tAtCrash, 0.5, 3.2) + tc, 26, 46 * u1, u1);
      break;
    }
    case "akimbo": { // guns fly and skid, hat flips, one last shot in the air
      // same scene scale-up as the celebration, so nothing shrinks at the crash
      ctx.save(); ctx.translate(x, y); ctx.scale(1.15, 1.15); ctx.translate(-x, -y);
      const gL = toss(tc, x - 22 * u1, y - 10 * u1, -70 * u1, -60 * u1, 320 * u1, floor, 0.35, 16);
      const gR = toss(tc, x + 22 * u1, y - 10 * u1, 80 * u1, -50 * u1, 320 * u1, floor, 0.35, -14);
      const h = toss(tc, x, y - headR - 4 * u1, 15 * u1, -110 * u1, 300 * u1, floor - 2 * u1, 0.2, 7);
      ctx.strokeStyle = "#FFB74D"; ctx.lineWidth = 3 * u1;
      for (const d of [-1, 1]) {
        const fl = eo(clamp01(tc / 0.4));
        ctx.beginPath(); ctx.moveTo(x + d * 6 * u1, y - 1 * u1); ctx.lineTo(x + d * (20 - 6 * fl) * u1, y + (-8 + 18 * fl) * u1); ctx.stroke();
      }
      ([[gL, -1], [gR, 1]] as [ReturnType<typeof toss>, number][]).forEach(([g, d]) => {
        ctx.save(); ctx.translate(g.x, g.y); ctx.rotate(g.rot); ctx.scale(d * u1, u1); revolver(ctx); ctx.restore();
      });
      ctx.save(); ctx.translate(h.x, h.y); ctx.rotate(h.settled ? Math.PI : h.rot); hat(ctx, 0, 0, 1.15 * u1); ctx.restore();
      const t0 = 0.5;
      if (tc > t0 && tc < t0 + 0.35) {
        const u = (tc - t0) / 0.35;
        ctx.strokeStyle = `rgba(255,255,255,${1 - u})`; ctx.lineWidth = 2 * u1;
        ctx.beginPath(); ctx.moveTo(gR.x + 4 * u1, gR.y - 6 * u1); ctx.lineTo(gR.x + 4 * u1, gR.y - 6 * u1 - 70 * u1 * u); ctx.stroke();
        ctx.fillStyle = `rgba(255,224,130,${1 - u})`; ctx.beginPath(); ctx.arc(gR.x + 4 * u1, gR.y - 8 * u1, 6 * u1 * (1 - u), 0, TAU); ctx.fill();
      }
      puff(ctx, gR.x + 4 * u1, gR.y - 14 * u1, (tc - t0) / 1.2, 14 * u1);
      ctx.restore();
      break;
    }
    case "fireworks": { // no new launches; shells already in the air finish where they are
      fireworksFrame(ctx, x, y, color, tAtCrash + tc, u1, false);
      break;
    }
    case "disco": { // the wire snaps, the ball bonks the head, bounces and rolls off
      const by = y - 50 * u1;
      ctx.strokeStyle = wireStroke(ctx, by - 20 * u1, by - 100 * u1); ctx.lineWidth = 1.2 * u1;
      ctx.beginPath(); ctx.moveTo(x, by - 100 * u1);
      ctx.quadraticCurveTo(x + Math.sin(tc * 6) * 8 * u1, by - 40 * u1, x + Math.sin(tc * 4) * 14 * u1, by - 20 * u1 + Math.min(20, tc * 30) * u1); ctx.stroke();
      const b = tc > 0.15 ? toss(tc - 0.15, x, by, 0, 0, 420 * u1, y - headR - 8 * u1, 0.55, 3) : { x, y: by };
      let bx = b.x, byy = b.y;
      if (tc > 0.6) { const u = tc - 0.6; bx = x + u * 55 * u1; byy = Math.min(floor - 9 * u1, y - headR - 8 * u1 + eo(clamp01(u / 0.5)) * (26 * u1 + headR - 1 * u1)); }
      ctx.save(); ctx.translate(bx, byy); ctx.rotate(tc * 3);
      ctx.fillStyle = "#c3cedd"; ctx.beginPath(); ctx.arc(0, 0, 9 * u1, 0, TAU); ctx.fill();
      ctx.strokeStyle = "rgba(255,255,255,.55)"; ctx.lineWidth = 0.7 * u1;
      for (let k = -2; k <= 2; k++) { ctx.beginPath(); ctx.ellipse(0, 0, 9 * u1, Math.abs(k) * 3 * u1 + 0.01, 0, 0, TAU); ctx.stroke(); }
      ctx.beginPath(); ctx.moveTo(0, -9 * u1); ctx.lineTo(0, 9 * u1); ctx.stroke(); ctx.restore();
      // the orbiting dots: frozen where the lap clock left them, then each
      // drops, bounces and rolls away in the direction it was travelling
      for (let k = 0; k < 12; k++) {
        const a = tAtCrash * 2 + k * (TAU / 12), rr = 34 * u1 + Math.sin(tAtCrash * 3 + k) * 10 * u1;
        const dx = x + Math.cos(a) * rr, dy = y + Math.sin(a) * rr * 0.55;
        const vx = -Math.sin(a) * 40 * u1 * (0.6 + rnd(k + 70) * 0.8);
        const d = toss(Math.max(0, tc - k * 0.05), dx, dy, vx, -20 * u1 * rnd(k + 71), 380 * u1, floor - 2.4 * u1, 0.45, 0);
        ctx.fillStyle = CONF[k % CONF.length];
        ctx.beginPath(); ctx.arc(d.x, d.y, 2.4 * u1, 0, TAU); ctx.fill();
      }
      break;
    }
    case "coronation": { // the crown slides off and rolls away like a dropped coin; the beam flickers out
      const fl = tc < 0.6 ? (Math.sin(tc * 40) > 0 ? 1 : 0.3) * (1 - tc / 0.6) : 0;
      if (fl > 0) {
        const g = ctx.createLinearGradient(0, y - 110 * u1, 0, y);
        g.addColorStop(0, `rgba(255,240,190,${0.26 * fl})`); g.addColorStop(1, "rgba(255,240,190,0)");
        ctx.fillStyle = g;
        ctx.beginPath(); ctx.moveTo(x - 9 * u1, y - 110 * u1); ctx.lineTo(x + 9 * u1, y - 110 * u1); ctx.lineTo(x + 26 * u1, y); ctx.lineTo(x - 26 * u1, y); ctx.closePath(); ctx.fill();
      }
      // the crown tips off the head, falls, bounces and skids to a stop, then
      // settles onto whichever side it landed nearest (upright, on its side,
      // or upside down)
      const c = toss(tc, x, y - headR - 16 * u1, 45 * u1, -30 * u1, 380 * u1, floor - 4 * u1, 0.3, 5);
      let landed = -1;
      for (let q = 0; q <= tc; q += 1 / 30) { if (toss(q, x, y - headR - 16 * u1, 45 * u1, -30 * u1, 380 * u1, floor - 4 * u1, 0.3, 5).settled) { landed = q; break; } }
      let rot = c.rot;
      if (landed >= 0) {
        const rest = Math.round(c.rot / (Math.PI / 2)) * (Math.PI / 2);
        rot = c.rot + (rest - c.rot) * eo(clamp01((tc - landed) / 0.35));
      }
      ctx.save(); ctx.translate(c.x, c.y); ctx.rotate(rot); crown(ctx, 0, 0, 1.32 * u1); ctx.restore();
      break;
    }
    case "trophy": { // it drops off its perch, hits the floor and shatters into shards
      const ty = y - 49 * u1, FALL = 0.42; // matches the celebration's hover height
      const fu = clamp01(tc / FALL), fy = ty + (floor - 6 * u1 - ty) * fu * fu;
      if (tc < FALL) {
        ctx.save(); ctx.translate(x, fy); ctx.rotate(fu * 0.6); ctx.scale(1.3 * u1, 1.3 * u1); trophyCup(ctx); trophyBase(ctx); ctx.restore();
      } else {
        const ts = tc - FALL;
        if (ts < 0.2) { // impact glint
          const g = ctx.createRadialGradient(x, floor - 6 * u1, 0, x, floor - 6 * u1, 22 * u1);
          g.addColorStop(0, `rgba(255,255,255,${1 - ts * 5})`); g.addColorStop(1, "rgba(255,240,190,0)");
          ctx.fillStyle = g; ctx.fillRect(x - 22 * u1, floor - 28 * u1, 44 * u1, 44 * u1);
        }
        for (let k = 0; k < 14; k++) { // gold shards fan out, tumble, skid and settle
          const a = -Math.PI * (0.15 + rnd(k + 500) * 0.7), sp = (40 + rnd(k + 501) * 90) * u1;
          const sh = toss(ts, x + (rnd(k + 502) - 0.5) * 10 * u1, floor - 8 * u1, Math.cos(a) * sp, Math.sin(a) * sp, 380 * u1, floor - 2 * u1, 0.3, (rnd(k + 503) - 0.5) * 30);
          const sz = (2 + rnd(k + 504) * 3.5) * 1.3 * u1; // shards match the larger cup
          ctx.save(); ctx.translate(sh.x, sh.y); ctx.rotate(sh.rot);
          ctx.fillStyle = k % 3 ? "#ffcf4d" : "#ffe9a3"; ctx.strokeStyle = "#b07d09"; ctx.lineWidth = 0.6 * u1;
          ctx.beginPath(); ctx.moveTo(-sz, sz * 0.5); ctx.lineTo(0, -sz); ctx.lineTo(sz * (0.5 + rnd(k + 505) * 0.6), sz * 0.6); ctx.closePath(); ctx.fill(); ctx.stroke();
          ctx.restore();
        }
        for (let k = 0; k < 8; k++) { // glitter dust
          const u = clamp01(ts / 0.9);
          const a = rnd(k + 600) * TAU, d = (10 + rnd(k + 601) * 30) * u1 * eo(u);
          ctx.globalAlpha = 1 - u; ctx.fillStyle = "#fff6d2";
          ctx.beginPath(); ctx.arc(x + Math.cos(a) * d, floor - 8 * u1 + Math.sin(a) * d * 0.4 - 10 * u1 * u, 1 * u1, 0, TAU); ctx.fill();
          ctx.globalAlpha = 1;
        }
      }
      // the sunburst sputters out as it falls
      const sb = Math.max(0, 1 - tc / 0.5) * (Math.sin(tc * 35) > 0 ? 1 : 0.2);
      if (sb > 0) {
        ctx.save(); ctx.translate(x, ty - 2 * u1); ctx.rotate(tc * 0.5);
        for (let k = 0; k < 10; k++) {
          ctx.rotate(TAU / 10);
          const g = ctx.createLinearGradient(0, 0, 0, -58 * u1);
          g.addColorStop(0, `rgba(255,207,77,${0.22 * sb})`); g.addColorStop(1, "rgba(255,207,77,0)");
          ctx.fillStyle = g; ctx.beginPath(); ctx.moveTo(0, 0); ctx.lineTo(6.5 * u1, -58 * u1); ctx.lineTo(-6.5 * u1, -58 * u1); ctx.closePath(); ctx.fill();
        }
        ctx.restore();
      }
      break;
    }
    case "champagne": { // bottles topple outward, jets die as they fall, the crown lands upside down
      const sc = 0.85 * u1;
      ([[-1, 0], [1, 0.35]] as [number, number][]).forEach(([sd, delay], i) => {
        const bx = x + sd * 66 * u1, by = y + 10 * u1, tilt0 = -sd * 0.5;
        // pivot about the bottle's base: it rocks, then tips over outward
        // SLOWLY (staggered per bottle), lands with a hop, and lies flat
        const baseX = bx - Math.sin(tilt0) * 16 * sc, baseY = by + Math.cos(tilt0) * 16 * sc;
        const td = Math.max(0, tc - delay);
        const fu = eo(clamp01(td / 1.2));
        const wobble = Math.sin(tc * 10) * 0.06 * (1 - fu);
        const ang = tilt0 + wobble - sd * (Math.PI / 2 - 0.5) * fu;
        const hop = td > 1.2 ? Math.abs(Math.sin((td - 1.2) * 14)) * 3 * u1 * Math.max(0, 1 - (td - 1.2) * 2.5) : 0;
        const cx = baseX + Math.sin(ang) * 16 * sc, cy = baseY - Math.cos(ang) * 16 * sc - hop;
        const tip = bottleTip(cx, cy, ang, sc);
        // the jet keeps going along the neck while it falls, tapering out
        // slowly enough to arc with the topple
        const p = Math.max(0, 1 - td / 1.8);
        if (p > 0) jet(ctx, tip.x, tip.y, tip.axis, p, tAtCrash + tc, 0, floor, 17 + i * 13, u1);
        bottle(ctx, cx, cy, ang, sc);
        // the dying dribble takes over as the jet fades and drips for a while
        if (td > 0.9) dribble(ctx, tip.x, tip.y, tip.axis, (td - 0.9) * 0.8, 3 + i * 6, u1);
      });
      const c = toss(tc, x, y - headR - 16 * u1, 20 * u1, -70 * u1, 300 * u1, floor - 3 * u1, 0.45, 6);
      ctx.save(); ctx.translate(c.x, c.y); ctx.rotate(c.settled ? Math.PI : c.rot); crown(ctx, 0, 0, 1.32 * u1); ctx.restore();
      break;
    }
  }
  ctx.restore();
}
